import { Suggestion, Suggestions } from "@/components/ai-elements/suggestion";
import {
  ChatComposerFrame,
  ChatComposerTextarea,
  ChatComposerToolbar,
} from "@/components/chat/chat-dialog-primitives";
import { stripResetPrefix } from "./conversation-utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { CardFooter } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTranslation } from "@/hooks/useTranslation";
import { CircleStopIcon, CornerDownLeft, SparklesIcon } from "lucide-react";
import { RefObject } from "react";
import { ConversationResetButton } from "./conversation-reset-button";

type ConversationFooterProps = {
  suggestions: string[];
  onSuggestionClick: (suggestion: string) => void;
  inputValue: string;
  onInputChange: (value: string) => void;
  inputRef: RefObject<HTMLTextAreaElement | null>;
  onSubmit: () => void;
  isStreaming: boolean;
  onStop: () => void;
  isReadyToGenerate: boolean;
  onGenerate: () => void;
  isResetting: boolean;
  onReset: () => void;
  messageCount: number;
};

export function ConversationFooter({
  suggestions,
  onSuggestionClick,
  inputValue,
  onInputChange,
  inputRef,
  onSubmit,
  isStreaming,
  onStop,
  isReadyToGenerate,
  onGenerate,
  isResetting,
  onReset,
  messageCount,
}: ConversationFooterProps): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <CardFooter className="flex flex-col gap-3 p-3 border-t">
      {suggestions.length > 0 && (
        <Suggestions className="w-full">
          {suggestions.map((s) => (
            <Suggestion
              key={s}
              suggestion={s}
              onClick={onSuggestionClick}
            >
              {stripResetPrefix(s)}
            </Suggestion>
          ))}
        </Suggestions>
      )}
      <form
        className="w-full"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
      >
        <ChatComposerFrame>
        <ChatComposerTextarea
          ref={inputRef}
          value={inputValue}
          onChange={(e) => onInputChange(e.target.value)}
          placeholder={t(
            "team.components.instruction.conversation.inputPlaceholder"
          )}
          disabled={isStreaming}
        />
        <ChatComposerToolbar>
          <div className="flex items-center gap-1 ml-auto">
            <ConversationResetButton
              isResetting={isResetting}
              isDisabled={isStreaming}
              onReset={onReset}
            />
            {!isReadyToGenerate && (
              <ForceGenerateButton
                isStreaming={isStreaming}
                messageCount={messageCount}
                onGenerate={onGenerate}
              />
            )}
            {isStreaming ? (
              <Button
                type="button"
                size="sm"
                variant="destructive"
                className="gap-1.5"
                onClick={() => onStop()}
              >
                {t(
                  "team.components.instruction.conversation.stopButton"
                )}
                <CircleStopIcon className="size-3.5" />
              </Button>
            ) : (
              <Button
                type="submit"
                size="sm"
                className="gap-1.5"
                disabled={!inputValue.trim()}
              >
                {t(
                  "team.components.instruction.conversation.sendButton"
                )}
                <CornerDownLeft className="size-3.5" />
              </Button>
            )}
          </div>
        </ChatComposerToolbar>
        </ChatComposerFrame>
      </form>
    </CardFooter>
  );
}

type ForceGenerateButtonProps = {
  isStreaming: boolean;
  messageCount: number;
  onGenerate: () => void;
};

/** Generate button with confirmation dialog, shown when AI hasn't deemed readiness. */
function ForceGenerateButton({
  isStreaming,
  messageCount,
  onGenerate,
}: ForceGenerateButtonProps): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <AlertDialog>
      <Tooltip>
        <TooltipTrigger asChild>
          <AlertDialogTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isStreaming || messageCount < 2}
            >
              <SparklesIcon className="mr-2 w-4 h-4" />
              {t(
                "team.components.instruction.conversation.generateButton"
              )}
            </Button>
          </AlertDialogTrigger>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-center">
          {t(
            "team.components.instruction.conversation.generateTooltip"
          )}
        </TooltipContent>
      </Tooltip>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t(
              "team.components.instruction.conversation.generateConfirm.title"
            )}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t(
              "team.components.instruction.conversation.generateConfirm.description"
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>
            {t(
              "team.components.instruction.conversation.generateConfirm.cancel"
            )}
          </AlertDialogCancel>
          <AlertDialogAction onClick={onGenerate}>
            {t(
              "team.components.instruction.conversation.generateConfirm.confirm"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
